import { adminApiClient } from '@/utils/apiClient'
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'

/**
 * Lightweight HTTP wrapper to keep a single place for admin API client
 * configuration and error logging. Logic is kept identical to the
 * existing axios usage.
 */
const withErrorHandling = async <T>(fn: () => Promise<T>): Promise<T> => {
  try {
    return await fn()
  } catch (error) {
    // Centralized log for admin API failures; rethrow to preserve behavior
    console.error('Admin API request failed', error)
    throw error
  }
}

export const resolveApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { error?: unknown; message?: unknown }
      | undefined
    const nestedErrorMessage = (
      typeof data?.error === 'object' &&
      data?.error &&
      'message' in data.error &&
      typeof (data.error as { message?: unknown }).message === 'string'
    )
      ? (data.error as { message: string }).message.trim()
      : ''
    if (nestedErrorMessage) {
      return nestedErrorMessage
    }
    const errorMessage = typeof data?.error === 'string' ? data.error.trim() : ''
    if (errorMessage) {
      return errorMessage
    }
    const message = typeof data?.message === 'string' ? data.message.trim() : ''
    if (message) {
      return message
    }
    if (typeof error.message === 'string' && error.message.trim().length > 0) {
      return error.message.trim()
    }
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message.trim()
  }
  return fallback
}

export const resolveBlobApiErrorMessage = async (
  error: unknown,
  fallback: string
): Promise<string> => {
  if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const rawText = await error.response.data.text()
      if (rawText.trim().length > 0) {
        try {
          const parsed = JSON.parse(rawText) as
            | {
                error?: string | { message?: string }
                message?: string
              }
            | undefined
          if (typeof parsed?.error === 'object' && typeof parsed.error?.message === 'string') {
            return parsed.error.message.trim()
          }
          if (typeof parsed?.error === 'string' && parsed.error.trim().length > 0) {
            return parsed.error.trim()
          }
          if (typeof parsed?.message === 'string' && parsed.message.trim().length > 0) {
            return parsed.message.trim()
          }
        } catch {
          return rawText.trim()
        }
      }
    } catch {
      // Fall through to the normal Axios error parsing path.
    }
  }

  return resolveApiErrorMessage(error, fallback)
}

const get = async <T>(url: string, config?: AxiosRequestConfig) =>
  withErrorHandling<AxiosResponse<T>>(() => adminApiClient.get<T>(url, config))

const post = async <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
  withErrorHandling<AxiosResponse<T>>(() => adminApiClient.post<T>(url, data, config))

const put = async <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
  withErrorHandling<AxiosResponse<T>>(() => adminApiClient.put<T>(url, data, config))

const del = async <T>(url: string, config?: AxiosRequestConfig) =>
  withErrorHandling<AxiosResponse<T>>(() => adminApiClient.delete<T>(url, config))

export const extractDownloadFilename = (
  contentDisposition: string | null | undefined,
  fallback: string
): string => {
  if (!contentDisposition) {
    return fallback
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1])
    } catch {
      return utf8Match[1]
    }
  }

  const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
  if (plainMatch?.[1]) {
    return plainMatch[1]
  }

  return fallback
}

export const triggerBrowserDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const baseService = {
  client: adminApiClient,
  get,
  post,
  put,
  delete: del,
}

export { adminApiClient, withErrorHandling }
